import { Channel, invoke } from "@tauri-apps/api/core";
import { relaunch } from "@tauri-apps/plugin-process";
import { atom } from "jotai";
import { atomWithRefresh } from "jotai/utils";

import { generateShortUuid } from "@moment/api-collab";
import { Logger } from "@moment/logging";

import { env } from "../env/env";
import { $internet } from "../utils";
import type { DownloadEvent, UpdateMetadata } from "./autoupdater.types";

const logger = new Logger("autoupdater");

const REFRESH_INTERVAL_MS = 300_000; // 5 minutes

export const $downloadStatus = atom(
	"idle" as "idle" | "downloading" | "ready" | "restarting" | "failed"
);
$downloadStatus.debugLabel = "autoupdater.downloadStatus";
export const $error = atom(undefined as string | undefined);
$error.debugLabel = "autoupdater.error";

const $downloadEvent = atom(undefined as DownloadEvent | undefined);
$downloadEvent.debugLabel = "downloadEvent";

const fetchUpdate = async () => {
	// for local dev, we do not auto update.
	if (env.appEnv === "local" || env.isSsr) {
		return {
			latestVersion: "local",
			currentVersion: "local",
		};
	}
	try {
		logger.info("Fetching update metadata");
		const result = await invoke<UpdateMetadata>("fetch_update");
		logger.info("Fetched update metadata", { result });
		return result;
	} catch (error) {
		// if we get an error,
		// log that error and return undefined.
		// try again later.
		logger.error("Failed to fetch update", { error });
		return;
	}
};

// Atom with auto-refresh logic
export const $updateMetadata = atomWithRefresh(async (_get) => {
	return await fetchUpdate();
});

$updateMetadata.debugLabel = "updateMetadata";

// Setup onMount for auto-refresh
$updateMetadata.onMount = (set) => {
	let lastCheckTime = 0;
	if (env.isSsr) {
		return;
	}

	const runCheck = () => {
		const now = Date.now();
		const timeSinceLastCheck = now - lastCheckTime;
		// Only run if enough time has passed since last check
		if (timeSinceLastCheck >= REFRESH_INTERVAL_MS) {
			lastCheckTime = now;
			set();
		}
	};
	// Then check every 5 minutes
	// Note: setInterval may fire rapidly when tab becomes active after being backgrounded
	// The timestamp check in runCheck prevents this from causing rapid requests
	const intervalId = setInterval(runCheck, REFRESH_INTERVAL_MS);

	return () => {
		clearInterval(intervalId);
	};
};

const $traceId = atom(undefined as string | undefined);
$traceId.debugLabel = "traceId";

export const $updater = atom(null, (get, set, versionDownloading: string) => {
	const isConnected = get($internet);
	if (!isConnected) {
		logger.debug("User is offline, skipping auto updater check");
		return;
	}

	if (!versionDownloading) {
		logger.error("Version downloading is required");
		return;
	}

	const tauriChannel = new Channel<DownloadEvent>(async (message) => {
		// do not log event to datadog as this is noisy.
		logger.debug("Download event received", {
			type: "autoUpdater.download.update",
			message,
		});

		set($downloadEvent, message);
		if (message.event === "finished") {
			logger.info("Download finished", {
				versionDownloading,
				traceId,
			});
			set($downloadStatus, "ready");
		}
		if (message.event === "error") {
			logger.error("Download error", {
				versionDownloading,
				traceId,
				message,
			});
			set($error, message.data.error);
			set($downloadStatus, "failed");
		}
	});

	let traceId = get($traceId);
	if (!traceId) {
		traceId = `tauri-updater_${generateShortUuid()}`;
		set($traceId, traceId);
	}

	logger.info("Starting download for version", {
		versionDownloading,
		traceId,
	});

	set($downloadStatus, "downloading");

	void invoke("prepare_update", {
		onEvent: tauriChannel,
		traceId,
		versionDownloading: versionDownloading,
	});
});
$updater.debugLabel = "autoupdater.updater";

export const $restartApp = atom(
	null,
	async (
		get,
		set,
		input: {
			versionDownloading: string;
		}
	) => {
		try {
			const { versionDownloading } = input;
			const downloadStatusAtom = $downloadStatus;

			const downloadEvent = get($downloadEvent);
			if (downloadEvent?.event !== "finished") {
				logger.error("Download event is not finished, cannot restart app");
				return;
			}

			const traceId = get($traceId) ?? `tauri-updater_${generateShortUuid()}`;
			set(downloadStatusAtom, "restarting");
			const tauriChannel = new Channel<DownloadEvent>(async (message) => {
				if (message.event === "error") {
					logger.error("Download error", {
						versionDownloading,
						traceId,
						message,
					});
					set($error, message.data.error);
					set($downloadStatus, "failed");
				}
			});
			await invoke("finalize_update", {
				onEvent: tauriChannel,
				traceId,
				versionDownloading,
			});

			// cleanup
			set($traceId, undefined);
			set($downloadEvent, undefined);

			await relaunch();
		} catch (error) {
			logger.error("Failed to restart app after update", { error });
			set($downloadStatus, "failed");
		}
	}
);

$restartApp.debugLabel = "autoupdater.restartApp";

// Atoms for startup. When the app first starts and there is an update available,
// we want to start downloading it right away and force a restart.
export const $updateAvailableOnStartup = atom(false);
$updateAvailableOnStartup.debugLabel = "autoupdater.updateAvailableOnStartup";

export const $updaterOnStartup = atom(null, async (get, set) => {
	const traceId = `tauri-updater_${generateShortUuid()}`;
	logger.debug("Running updater on startup", { traceId });
	try {
		const isConnected = get($internet);
		if (!isConnected) {
			logger.debug("User is offline, skipping auto updater check");
			return;
		}

		const updateMetadata = await get($updateMetadata);
		if (!updateMetadata || updateMetadata.latestVersion === updateMetadata.currentVersion) {
			return;
		}

		set($updateAvailableOnStartup, true);
	} catch (error) {
		logger.error("Failed to fetch update metadata on startup", { error, traceId });
	}
});
$updaterOnStartup.debugLabel = "autoupdater.updaterOnStartup";
